/**
 * Daily Facts Service
 * Handles all CRUD operations for the facts system
 */

import { safeFrom, isSupabaseAvailable } from '@/lib/safe-supabase';
import { 
  ServiceResponse, 
  successResponse, 
  errorResponse, 
  handleServiceError
} from '@/lib/service-errors';
import { auditContent } from '@/lib/audit-logger';
import type {
  DailyFact,
  DailyFactFormValues,
  GetFactsOptions,
  FactsStats,
  FactsFilter,
  FactValidationResult,
  CreateFactResponse,
  UpdateFactResponse,
  DeleteFactResponse,
  FactCategory
} from '@/types/facts';

/**
 * Schema detection for daily_facts to support both "simple" and "extended" schemas.
 * - Simple schema columns: id, fact_text, category, source, is_active, created_at, updated_at
 * - Extended schema adds: title, featured, active, display_date, priority, tags, image_url, source_url, etc.
 */
type FactsSchemaInfo = {
  hasFeatured: boolean;
  hasActive: boolean;
  hasIsActive: boolean;
  hasPriority: boolean;
  hasTags: boolean;
};

let factsSchemaCache: FactsSchemaInfo | null = null;

async function ensureFactsSchema(): Promise<FactsSchemaInfo> {
  if (factsSchemaCache) return factsSchemaCache;

  // Probe one row; if table empty, assume simple baseline (is_active)
  if (!isSupabaseAvailable()) {
    factsSchemaCache = { hasFeatured: false, hasActive: false, hasIsActive: true, hasPriority: false, hasTags: false };
    return factsSchemaCache;
  }
  const { data } = await safeFrom('daily_facts').select('*').limit(1);
  const sample = (data && data[0]) ? data[0] : {};

  const hasFeatured = Object.prototype.hasOwnProperty.call(sample, 'featured');
  const hasActive = Object.prototype.hasOwnProperty.call(sample, 'active');
  // If neither active nor is_active detected, assume simple schema's is_active
  const hasIsActive = hasActive ? false : true;
  const hasPriority = Object.prototype.hasOwnProperty.call(sample, 'priority');
  const hasTags = Object.prototype.hasOwnProperty.call(sample, 'tags');

  factsSchemaCache = { hasFeatured, hasActive, hasIsActive, hasPriority, hasTags };
  return factsSchemaCache;
}

/**
 * Facts Service - Main interface for daily facts management
 */
export const factsService = {
  /**
   * Get all daily facts with optional filtering
   * @param options - Filtering and pagination options
   * @returns Promise<DailyFact[]>
   */
  async getFacts(options: GetFactsOptions = {}): Promise<ServiceResponse<DailyFact[]>> {
    if (!isSupabaseAvailable()) {
      return successResponse([]);
    }

    try {
      const schema = await ensureFactsSchema();

      let query: any = safeFrom('daily_facts')
        .select('*');

      // Apply filters
      if (!options.isAdmin) {
        if (schema.hasActive) {
          query = query.eq('active', true);
        } else {
          query = query.eq('is_active', true);
        }
      }

      if (options.category) {
        query = query.eq('category', options.category);
      }

      if (options.featured !== undefined && schema.hasFeatured) {
        query = query.eq('featured', options.featured);
      }

      if (options.active !== undefined) {
        if (schema.hasActive) {
          query = query.eq('active', options.active);
        } else {
          query = query.eq('is_active', options.active);
        }
      }

      if (options.search) {
        // Only title exists in extended schema; fallback to fact_text + source in both
        const ors: string[] = [];
        // try title (extended)
        ors.push(`title.ilike.%${options.search}%`);
        // always try fact_text and source
        ors.push(`fact_text.ilike.%${options.search}%`);
        ors.push(`source.ilike.%${options.search}%`);
        query = query.or(ors.join(','));
      }

      if (options.tags && options.tags.length > 0 && (await ensureFactsSchema()).hasTags) {
        query = query.overlaps('tags', options.tags);
      }

      // Pagination
      if (options.limit) {
        query = query.limit(options.limit);
      }
      if (options.offset) {
        query = query.range(options.offset, options.offset + (options.limit || 20) - 1);
      }

      // Ordering
      if ((await ensureFactsSchema()).hasPriority) {
        query = query.order('priority', { ascending: false });
      }
      query = query.order('created_at', { ascending: false });

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      return successResponse((data || []) as DailyFact[]);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'factsService.getFacts'));
    }
  },

  /**
   * Get a single daily fact by ID
   * @param id - Fact ID
   * @param isAdmin - Whether admin access is available
   * @returns Promise<DailyFact | null>
   */
  async getFactById(id: string | number, isAdmin = false): Promise<DailyFact | null> {
    try {
      const schema = await ensureFactsSchema();
      let query: any = safeFrom('daily_facts')
        .select('*')
        .eq('id', id)
        .maybeSingle();

      if (!isAdmin) {
        if (schema.hasActive) {
          query = query.eq('active', true);
        } else {
          query = query.eq('is_active', true);
        }
      }

      const { data, error } = await query;

      if (error) {
        if ((error as any).code === 'PGRST116') {
          return successResponse(null);
        }
        throw error;
      }

      if (!data) {
        return successResponse(null);
      }

      return successResponse(data as DailyFact);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'factsService.getFactById', { id }));
    }
  },

  /**
   * Get today's daily fact using the database function (extended schema),
   * falls back to random fact when function is missing.
   * @param date - Specific date (defaults to today)
   * @returns Promise<DailyFact | null>
   */
  async getTodaysFact(date?: string): Promise<ServiceResponse<DailyFact | null>> {
    if (!isSupabaseAvailable()) {
      return successResponse(null);
    }

    try {
      const targetDate = date || new Date().toISOString().split('T')[0];

      // Note: RPC calls need direct supabase access, but we'll handle errors gracefully
      const { supabase } = await import('@/lib/supabase');
      if (!supabase) {
        return this.getRandomFact();
      }

      const { data, error } = await supabase.rpc('get_daily_fact', { target_date: targetDate });

      if (error) {
        // Fallback to random fact
        return this.getRandomFact();
      }

      const fact = data && (data as any[]).length > 0 ? (data as any[])[0] as DailyFact : null;
      return successResponse(fact);
    } catch (error) {
      // Fallback to random fact
      return this.getRandomFact();
    }
  },

  /**
   * Get a random active fact as fallback
   * @returns Promise<DailyFact | null>
   */
  async getRandomFact(): Promise<ServiceResponse<DailyFact | null>> {
    try {
      const result = await this.getFacts({
        isAdmin: false,
        featured: true,
        limit: 10
      });

      if (!result.success || result.data.length === 0) {
        return successResponse(null);
      }

      const randomIndex = Math.floor(Math.random() * result.data.length);
      return successResponse(result.data[randomIndex]);
    } catch (error) {
      return successResponse(null);
    }
  },

  /**
   * Create a new daily fact
   * @param data - Fact form data
   * @returns Promise<ServiceResponse<DailyFact>>
   */
  async createFact(data: DailyFactFormValues): Promise<ServiceResponse<DailyFact>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(handleServiceError(new Error('Supabase not configured'), 'factsService.createFact'));
    }

    try {
      // Validate data
      const validation = this.validateFact(data);
      if (!validation.valid) {
        return errorResponse(handleServiceError(
          new Error('Validation failed: ' + Object.values(validation.errors).join(', ')),
          'factsService.createFact'
        ));
      }

      const schema = await ensureFactsSchema();

      // Prepare data for insertion using only available columns
      const insertData: Record<string, any> = {
        fact_text: data.fact_text.trim(),
        category: data.category,
      };

      // Optional extended fields
      if (typeof data.title === 'string') insertData.title = data.title.trim();
      if (data.source !== undefined) insertData.source = data.source?.trim() || null;
      if (data.source_url !== undefined) insertData.source_url = data.source_url?.trim() || null;
      if (data.image_url !== undefined) insertData.image_url = data.image_url?.trim() || null;
      if (schema.hasFeatured && data.featured !== undefined) insertData.featured = data.featured;
      if (schema.hasActive && data.active !== undefined) insertData.active = data.active;
      if (!schema.hasActive && schema.hasIsActive) insertData.is_active = data.active !== undefined ? data.active : true;
      if (schema.hasPriority && data.priority !== undefined) insertData.priority = data.priority;
      if (typeof data.display_date === 'string') insertData.display_date = data.display_date || null;
      if (schema.hasTags && Array.isArray(data.tags)) insertData.tags = data.tags;

      const { data: fact, error } = await safeFrom('daily_facts')
        .insert([insertData])
        .select()
        .single();

      if (error) {
        throw error;
      }

      const createdFact = fact as DailyFact;

      // Audit log
      await auditContent.create('daily_fact', createdFact.id.toString(), {
        title: createdFact.title,
        category: createdFact.category,
      });

      return successResponse(createdFact);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'factsService.createFact'));
    }
  },

  /**
   * Update an existing daily fact
   * @param id - Fact ID
   * @param data - Updated fact data
   * @returns Promise<ServiceResponse<DailyFact>>
   */
  async updateFact(id: string | number, data: Partial<DailyFactFormValues>): Promise<ServiceResponse<DailyFact>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(handleServiceError(new Error('Supabase not configured'), 'factsService.updateFact'));
    }

    try {
      // Validate data if provided
      if (Object.keys(data).length > 0) {
        const validation = this.validateFact(data, false);
        if (!validation.valid) {
          return errorResponse(handleServiceError(
            new Error('Validation failed: ' + Object.values(validation.errors).join(', ')),
            'factsService.updateFact'
          ));
        }
      }

      const schema = await ensureFactsSchema();

      // Prepare update data with supported columns
      const updateData: Record<string, any> = {};

      if (data.title !== undefined) updateData.title = data.title.trim();
      if (data.fact_text !== undefined) updateData.fact_text = data.fact_text.trim();
      if (data.category !== undefined) updateData.category = data.category;
      if (data.source !== undefined) updateData.source = data.source?.trim() || null;
      if (data.source_url !== undefined) updateData.source_url = data.source_url?.trim() || null;
      if (data.image_url !== undefined) updateData.image_url = data.image_url?.trim() || null;
      if (schema.hasFeatured && data.featured !== undefined) updateData.featured = data.featured;
      if (data.active !== undefined) {
        if (schema.hasActive) {
          updateData.active = data.active;
        } else {
          updateData.is_active = data.active;
        }
      }
      if (data.display_date !== undefined) updateData.display_date = data.display_date || null;
      if (schema.hasPriority && data.priority !== undefined) updateData.priority = data.priority;
      if (schema.hasTags && data.tags !== undefined) updateData.tags = data.tags;

      const { data: fact, error } = await safeFrom('daily_facts')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) {
        throw error;
      }

      const updatedFact = fact as DailyFact;

      // Audit log
      await auditContent.update('daily_fact', id.toString(), {}, {
        title: updatedFact.title,
        category: updatedFact.category,
      });

      return successResponse(updatedFact);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'factsService.updateFact', { id }));
    }
  },

  /**
   * Delete a daily fact
   * @param id - Fact ID
   * @returns Promise<ServiceResponse<boolean>>
   */
  async deleteFact(id: string | number): Promise<ServiceResponse<boolean>> {
    if (!isSupabaseAvailable()) {
      return errorResponse(handleServiceError(new Error('Supabase not configured'), 'factsService.deleteFact'));
    }

    try {
      const { error } = await safeFrom('daily_facts')
        .delete()
        .eq('id', id);

      if (error) {
        throw error;
      }

      // Audit log
      await auditContent.delete('daily_fact', id.toString(), { id });

      return successResponse(true);
    } catch (error) {
      return errorResponse(handleServiceError(error, 'factsService.deleteFact', { id }));
    }
  },

  /**
   * Get statistics about daily facts for admin dashboard
   * @returns Promise<FactsStats>
   */
  async getFactsStats(): Promise<FactsStats> {
    if (!isSupabaseAvailable()) {
      // Return empty stats if Supabase is not configured
      return {
        total: 0,
        active: 0,
        featured: 0,
        byCategory: {} as Record<FactCategory, number>,
        recentlyAdded: 0
      };
    }

    try {
      const schema = await ensureFactsSchema();

      const fields: string[] = ['category', 'created_at'];
      const activeField = schema.hasActive ? 'active' : 'is_active';
      fields.push(activeField);
      if (schema.hasFeatured) fields.push('featured');

      const { data: facts, error } = await safeFrom('daily_facts')
        .select(fields.join(','));

      if (error) {
        console.error('Error fetching facts stats:', error);
        throw new Error(`Failed to fetch stats: ${error.message}`);
      }

      const stats: FactsStats = {
        total: facts?.length || 0,
        active: facts?.filter((f: any) => !!f[activeField]).length || 0,
        featured: schema.hasFeatured ? (facts?.filter((f: any) => !!f.featured).length || 0) : 0,
        byCategory: {} as Record<FactCategory, number>,
        recentlyAdded: 0
      };

      // Count by category
      const categories: FactCategory[] = ['General', 'Historical', 'People', 'Events', 'Traditions', 'Arts', 'Music', 'Food', 'Community', 'Heritage'];
      categories.forEach(category => {
        stats.byCategory[category] = facts?.filter((f: any) => f.category === category).length || 0;
      });

      // Count recently added (last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      stats.recentlyAdded = facts?.filter((f: any) =>
        new Date(f.created_at) > thirtyDaysAgo
      ).length || 0;

      return stats;
    } catch (error) {
      console.error('Facts stats service error:', error);
      throw new Error('Unable to load facts statistics');
    }
  },

  /**
   * Toggle "featured" flag (no-op if column not present)
   */
  async toggleFeatured(id: string | number, value: boolean): Promise<void> {
    const schema = await ensureFactsSchema();
    if (!schema.hasFeatured) {
      // No-op to keep UI flow working on simple schema
      return;
    }
    const { error } = await supabase
      .from('daily_facts')
      .update({ featured: value })
      .eq('id', id);
    if (error) throw error;
  },

  /**
   * Toggle "active" flag (supports active or is_active depending on schema)
   */
  async toggleActive(id: string | number, value: boolean): Promise<void> {
    const schema = await ensureFactsSchema();
    const update: any = schema.hasActive ? { active: value } : { is_active: value };
    const { error } = await supabase
      .from('daily_facts')
      .update(update)
      .eq('id', id);
    if (error) throw error;
  },

  /**
   * Validate fact data
   * @param data - Fact data to validate
   * @param isCreate - Whether this is for creation (all fields required)
   * @returns FactValidationResult
   */
  validateFact(data: Partial<DailyFactFormValues>, isCreate = true): FactValidationResult {
    const errors: any = {};

    // Title validation
    if (isCreate && !data.title) {
      errors.title = 'Title is required';
    } else if (data.title && (data.title.length < 10 || data.title.length > 200)) {
      errors.title = 'Title must be between 10 and 200 characters';
    }

    // Fact text validation
    if (isCreate && !data.fact_text) {
      errors.fact_text = 'Fact text is required';
    } else if (data.fact_text && (data.fact_text.length < 50 || data.fact_text.length > 1000)) {
      errors.fact_text = 'Fact text must be between 50 and 1000 characters';
    }

    // Category validation
    if (isCreate && !data.category) {
      errors.category = 'Category is required';
    }

    // Source URL validation
    if (data.source_url && data.source_url.trim()) {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(data.source_url)) {
        errors.source_url = 'Source URL must be a valid HTTP/HTTPS URL';
      }
    }

    // Display date validation
    if (data.display_date && data.display_date.trim()) {
      const date = new Date(data.display_date);
      if (isNaN(date.getTime())) {
        errors.display_date = 'Display date must be a valid date';
      }
    }

    // Priority validation
    if (data.priority !== undefined && (data.priority < 0 || data.priority > 10)) {
      errors.priority = 'Priority must be between 0 and 10';
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors
    };
  },
};
